<!-- FlashMessage.vue -->
<script setup lang="ts">
    import { reactive, watch, computed } from 'vue';
    import { timeout } from '../../lib/ChoiceUtils';
    import KeyMonitor from '../KeyMonitor.vue';
    import { DisplayStyle, IconTypes } from '../../Enums/UiEnums';
    import Icon from '../Icon.vue';

    type FlashMessageDisplayStyle = DisplayStyle.Primary | DisplayStyle.Success | DisplayStyle.Warning | DisplayStyle.Danger;

    const props = withDefaults(defineProps<{
        showMessage?: boolean,
        timeoutInMilliSec?: number,
        displayStyle?: FlashMessageDisplayStyle
    }>(), {
        timeoutInMilliSec: 1000,
        displayStyle: DisplayStyle.Primary
    });

    defineExpose({show});

    const emit = defineEmits<{
        "update:showMessage" : [showMessage: boolean]
    }>();

    const compData = reactive<{
        showMessage?: boolean
    }>({});

    watch(() =>  props.showMessage, (newVal, _oldVal) => {
        compData.showMessage = newVal;

        if (newVal)
        {
            timeout(props.timeoutInMilliSec).then(() =>{
                hideMessage();
            });
        }
    });

    // Display the message.  If no timeout value is passed we use the component's default timeout
    function show(timeoutInMilliSec?: number): void
    {
        compData.showMessage = true;
        timeout(timeoutInMilliSec ?? props.timeoutInMilliSec).then(() =>{
                hideMessage();
            });
    }

    function hideMessage()
    {
        compData.showMessage = false;
        emit("update:showMessage", false);
    }

    function listenForEscape(event: KeyboardEvent)
    {
        if ("Escape" === event.key)
        {
            hideMessage();
        }
    }

    const displayStyleDict: Record<FlashMessageDisplayStyle, string> = {
        [DisplayStyle.Primary]: "messageBoxPrimary",
        [DisplayStyle.Success]: "messageBoxSuccess",
        [DisplayStyle.Warning]: "messageBoxWarning",
        [DisplayStyle.Danger]: "messageBoxDanger",
    };

    const displayStyleClass = computed(() => {
        return displayStyleDict[props.displayStyle] ?? displayStyleDict[DisplayStyle.Primary];
    });
</script>

<style scoped>
    .messageBox {
        position: fixed;
        z-index: 999;
        top: 50%;
        left: 50%;
        width: 300px;
        margin-left: -150px;
        text-align: center;
        vertical-align: middle;
    }

    .messageBoxPrimary {
        background-color: lightgray;
        border: solid 1px black;
    }

    .messageBoxSuccess {
        background-color: palegreen;
        border: solid 1px green;
    }

    .messageBoxWarning {
        background-color: palegoldenrod;
        border: solid 1px goldenrod;
    }

    .messageBoxDanger {
        background-color: salmon;
        border: solid 1px red;        
    }

</style>

<template>
    <KeyMonitor @keydown="listenForEscape" />

    <Teleport to="body">
        <div v-if="compData.showMessage" class="messageBox" :class="displayStyleClass">
            <slot>
                <Icon :icon="IconTypes.Notification" :displayStyle="DisplayStyle.Dark"/>
            </slot>
        </div>
      </Teleport>    
</template>